import { FacetOptions } from '../../../b2b-order/retailer-platform/order/types';

export interface Facet {
    key: string;
    count: number;
}

export interface FacetWithName extends Facet {
    name: string;
}

export type ProductSearchResponse = ProductSearchResult | ProductSearchError;

export enum ProductSearchErrorType {
    AccessDenied = 'accessDenied',
    Unknown = 'unknown',
    MissingBrand = 'brandRequired'
}

export enum ProductSearchWarningType {
    SearchInputValidation = 'searchInputValidation'
}

export interface ProductSearchError {
    error?: ProductSearchErrorType;
    warning?: ProductSearchWarningType;
}

export interface ProductSearchResult {
    products: {
        colorGroupId: string;
        styleGroupId: string;
        variants: Product[];
    }[];
    count: number;
    facets?: {
        seasons: Facet[];
        targetGroups: Facet[];
        targetAndAgeGroups: Facet[];
        mainCategories: Facet[];
        ageGroups: Facet[];
        colors: Facet[];
        sustainableProducts: Facet[];
    };
}

export const isProductSearchResult = (
    productSearchResponse: ProductSearchResponse
): productSearchResponse is ProductSearchResult => (productSearchResponse as ProductSearchResult).count !== undefined;

export interface ArticleForOrder {
    articleNumber: string;
    brandId: string;
    brandName: string;
    imageId: string;
    name: string;
    colorName: string;
    colorCode: string;
    styleGroupId: string;
    colorGroupId: string;
    isPreliminary: boolean;
    price?: Price;
    isStockAvailable: boolean;
    isSelected?: boolean;
    selectedIndex?: number;
    gtins?: string[] | { gtin: string; targetQuantity: number; startDate?: string; endDate?: string }[];
    visibility: Record<ArticleVisibility, boolean>;
}

export enum ArticleVisibility {
    ContentPlatform = 'contentPlatform',
    EndlessAisle = 'endlessAisle',
    OrderPlatform = 'orderPlatform',
    PreorderPlatform = 'preorderPlatform'
}

export interface MinimumOrderRestriction {
    type: OrderRestrictionType;
    parameter: MinimumOrderVolumeParameter | number;
}

export interface MinimumOrderVolumeParameter {
    [currencyKey: string]: number;
}

export enum OrderRestrictionType {
    AmountOfItems = 'amountOfItems',
    OrderVolume = 'orderVolume'
}

export interface ArticlesForOrderSearchResult {
    articles: ArticleForOrder[];
    count: number;
    facets?: FacetOptions;
    sortingCriteria?: Sorting;
    errorRaised?: boolean;
}

export interface Price {
    currency?: string;
    discountedPurchasePrice?: number;
    purchasePrice?: number;
    recommendedRetailPrice?: number;
    rrpCurrency?: string;
    discount?: number;
}

export interface Product {
    _id: string;
    articleNumber: string;
    articleNumberSimplified: string;
    brand: string;
    colorGroupId: string;
    gtin: string;
    created: Date;
    fcAttributes: any;
    fcKeys: any;
    hasFrontImage: boolean;
    hasImages: boolean;
    hash: string;
    manufacturerAttributes: any;
    media: any;
    organization: string;
    state: string;
    styleGroupId: string;
    updated: Date;
    visibleFromDate: Date;
}

interface Pagination {
    pagination: {
        offset: number;
        limit?: number;
    };
}

export interface SearchParameters extends Pagination {
    query: string;
    nextId?: string;
    filters: {
        brands?: string[];
        brand?: string;
        categories?: string[];
        seasons?: string[];
        subSeasons?: string[];
        ageGroups?: string[];
        targetGroups?: string[];
        targetAndAgeGroups?: string[];
        colors?: string[];
        minimumDiscount?: string | null;
        minimumStock?: string | null;
        sizes?: string[];
        generalSizes?: string[];
        widths?: string[];
        lengths?: string[];
        fits?: string[];
        deliveryDates?: string[];
        mainCategory?: string;
        level1Category?: string;
        hasSustainabilityAttributes?: boolean;
        orderedArticles?: boolean;
        awaitingInitialDelivery?: boolean;
        retailers?: string[];
        orderTypes?: string[];
        orderDates?: string;
        period?: string;
        catalogs?: string[];
        includeArticles?: string[];
        activeStatuses?: string[];
    };
    sortingCriteria?: Sorting;
}

export interface SuggestionSearchParameters extends Pagination {
    query: string;
    filters: {
        brandId: string;
        retailerId: string;
        storeGln?: string;
        seasons?: string[];
        targetGroups?: string[];
        colors?: string[];
        excludeArticles: { styleGroupId: string; colorGroupId: string }[];
        period?: string;
        dateRange: { from: string; to: string } | undefined;
        withRetailerStockOnly: boolean;
        withBrandStockOnly?: boolean;
    };
    sortingCriteria?: Sorting;
}

export interface AutoRestockSearchParameters extends Pagination {
    query: string;
    filters: {
        brandId: string;
        retailerId: string;
        storeGln?: string;
        seasons?: string[];
        targetGroups?: string[];
        colors?: string[];
        excludeArticles: { styleGroupId: string; colorGroupId: string }[];
        period: string;
        dateRange: { from: string; to: string } | undefined;
        withRetailerStockOnly: boolean;
        withBrandStockOnly?: boolean;
    };
    sortingCriteria: Sorting;
}

export type SortingCriteria = {
    text: string;
    value: Sorting;
};

export enum ReorderSortBy {
    StockRatio = 'stockRatio',
    Discount = 'discount',
    PurchasePrice = 'purchasePrice',
    RRP = 'recommendedRetailPrice',
    ArticleNumber = 'articleNumber',
    NOS = 'nos',
    SEASON = 'season',
    CurrentSeason = 'currentSeason'
}

export enum SmartReplenishmentSortBy {
    ItemsSold = 'items_sold',
    StockTurn = 'stock_turn',
    SellThrough = 'sell_through',
    LatestInventory = 'latest_inventory',
    ArticleNumberColor = 'article_number_color'
}

export type SortBy = ReorderSortBy | SmartReplenishmentSortBy;

export enum SortOrder {
    ASC = 'asc',
    DESC = 'desc'
}

export type Sorting = {
    sortBy: SortBy;
    sortOrder: SortOrder;
};
